/*
 * Card slider: horizontal scroll-snap track where the centered card is "active"
 * (full color); side cards peek in from the edges. Used by sections/card-slider.liquid.
 */
if (!customElements.get('card-slider')) {
  class CardSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.card-slider__track');
      this.cards = Array.from(this.querySelectorAll('.card-slider__card'));
      this.dots = Array.from(this.querySelectorAll('[data-card-slider-dot]'));
      this.activeIndex = -1;

      if (!this.track || this.cards.length === 0) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = () => { if (this.activeIndex >= 0) this.goTo(this.activeIndex, false); };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      this.querySelector('[data-card-slider-prev]')?.addEventListener('click', () => this.goTo(this.activeIndex - 1));
      this.querySelector('[data-card-slider-next]')?.addEventListener('click', () => this.goTo(this.activeIndex + 1));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.cardSliderDot))));

      // Clicking a side card brings it to the center instead of following its link
      this.cards.forEach((card, index) => {
        card.addEventListener('click', (event) => {
          if (index !== this.activeIndex) {
            event.preventDefault();
            this.goTo(index);
          } else if (!card.hasAttribute('href')) {
            event.preventDefault();
          }
        });
      });

      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.goTo(this.activeIndex - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.goTo(this.activeIndex + 1); }
      });

      // Pause auto-rotate while the visitor interacts
      ['mouseenter', 'focusin', 'touchstart'].forEach((type) => this.addEventListener(type, () => this.stopAutoplay(), { passive: true }));
      ['mouseleave', 'focusout'].forEach((type) => this.addEventListener(type, () => this.startAutoplay()));

      // Theme editor: show the selected block
      this.onBlockSelect = (event) => {
        const index = this.cards.indexOf(event.target);
        if (index !== -1) { this.stopAutoplay(); this.goTo(index); }
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      // Start on the second card so both sides show a peeking card, like the reference design
      const startIndex = this.cards.length > 2 ? 1 : 0;
      requestAnimationFrame(() => {
        this.goTo(startIndex, false);
        this.startAutoplay();
      });
    }

    disconnectedCallback() {
      this.stopAutoplay();
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    goTo(index, smooth = true) {
      const count = this.cards.length;
      const target = this.cards[((index % count) + count) % count];
      const left = target.offsetLeft - (this.track.clientWidth - target.offsetWidth) / 2;

      this.track.scrollTo({ left, behavior: smooth && !this.reducedMotion ? 'smooth' : 'auto' });
      this.setActive(this.cards.indexOf(target));
    }

    onScroll() {
      if (this.scrollFrame) return;

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        const center = this.track.scrollLeft + this.track.clientWidth / 2;
        let closest = 0;
        let closestDistance = Infinity;

        this.cards.forEach((card, index) => {
          const distance = Math.abs(card.offsetLeft + card.offsetWidth / 2 - center);
          if (distance < closestDistance) { closestDistance = distance; closest = index; }
        });

        this.setActive(closest);
      });
    }

    setActive(index) {
      if (index === this.activeIndex) return;
      this.activeIndex = index;

      this.cards.forEach((card, i) => {
        card.classList.toggle('is-active', i === index);
        card.setAttribute('tabindex', i === index ? '0' : '-1');
      });

      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    startAutoplay() {
      const seconds = Number(this.dataset.autoplay);
      if (!seconds || this.cards.length < 2 || this.reducedMotion || window.Shopify?.designMode) return;

      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => this.goTo(this.activeIndex + 1), seconds * 1000);
    }

    stopAutoplay() {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  customElements.define('card-slider', CardSlider);
}
